import { useState, useEffect } from 'react';
import { Button, Checkbox, Stack, Text, TagsInput, Select, Textarea } from '@mantine/core';
import { IconCalendarEvent, IconMailForward, IconReportMoney } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import CardModal from './card-modal';
import { useSendBalanceEmail, useGetTenantBusiness, useTariffEmailLogs } from '@/hooks/api-hooks';
import { Business, Person } from '@turizi/shared';
import { localTenant } from '@/services/session-service';
import dayjs from 'dayjs';

interface Props {
    business?: Business;
    person?: Person;
    startDay?: string;
    endDay?: string;
}

export default function SendBalanceInput({ business, person, startDay, endDay }: Props): JSX.Element {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [paymentStatuses, setPaymentStatuses] = useState<string[]>(['paid', 'unpaid', 'partial']);

    const tenant = localTenant();
    const senderEmails = Array.from(
        new Set([
            tenant?.settings?.email,
            ...(tenant?.settings?.sender_emails || [])
        ].filter(Boolean))
    );
    const [selectedFromEmail, setSelectedFromEmail] = useState<string>(senderEmails[0] || '');

    useEffect(() => {
        if (senderEmails.length > 0 && !selectedFromEmail) {
            setSelectedFromEmail(senderEmails[0]);
        }
    }, [senderEmails]);

    // Fetch tenant business relation if it is a business
    const { data: tenantBusinessRes } = useGetTenantBusiness(
        tenant?.id,
        business?.id,
        { enabled: !!business?.id && opened }
    );
    const tenantBusinessId = tenantBusinessRes?.data?.id;

    // Fetch email logs for this tenant business if it is a business
    const { data: logsRes, isLoading: isLoadingLogs } = useTariffEmailLogs(
        tenantBusinessId,
        { enabled: !!tenantBusinessId && opened }
    );

    // Filter logs to show only balance statements
    const logs = (logsRes?.data || []).filter((log: any) => log.template_used === 'balance');

    // Extract existing email addresses safely
    let existingEmails: string[] = [];
    if (business && business.emails) {
        existingEmails = Object.values(business.emails).filter(Boolean);
    } else if (person && person.email) {
        existingEmails = [person.email];
    }

    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [manualEmails, setManualEmails] = useState<string[]>([]);
    const [customMessage, setCustomMessage] = useState<string>('');

    const sendMutation = useSendBalanceEmail();

    const togglePaymentStatus = (status: string) => {
        if (paymentStatuses.includes(status)) {
            setPaymentStatuses(paymentStatuses.filter(s => s !== status));
        } else {
            setPaymentStatuses([...paymentStatuses, status]);
        }
    };

    const handleSend = () => {
        const allEmails = [...selectedEmails, ...manualEmails].filter(Boolean);
        if (allEmails.length === 0 || paymentStatuses.length === 0) return;

        sendMutation.mutate({
            clientId: business?.id || person?.id || 0,
            clientType: business ? 'business' : 'person',
            emails: allEmails,
            startDay,
            endDay,
            fromEmail: selectedFromEmail,
            tenantBusinessId,
            paymentStatusFilter: paymentStatuses,
            customMessage: customMessage.trim() || undefined
        }, {
            onSuccess: () => {
                setOpened(false);
                setManualEmails([]);
                setSelectedEmails([]);
                setCustomMessage('');
            }
        });
    };

    const hasAnyEmail = existingEmails.length > 0;
    const isPeriod = Boolean(startDay || endDay);

    return (
        <>
            <Button
                leftSection={<IconMailForward size={16} />}
                size="xs"
                variant={isPeriod ? "filled" : "light"}
                color={isPeriod ? "black" : "black"}
                onClick={() => {
                    setSelectedEmails([]);
                    setCustomMessage('');
                    setOpened(true);
                }}
                className={isPeriod ? "font-semibold border border-indigo-200" : "font-semibold"}
            >
                {isPeriod ? t('send_period_balance_email') : t('send_all_balance_email')}
            </Button>

            <CardModal
                opened={opened}
                onClose={() => setOpened(false)}
                header={
                    <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${isPeriod ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-800'}`}>
                            {isPeriod ? <IconCalendarEvent size={18} /> : <IconReportMoney size={18} />}
                        </div>
                        <div className="font-bold text-lg text-slate-800">
                            {isPeriod ? t('send_period_balance_email') : t('send_all_balance_email')}
                        </div>
                    </div>
                }
                size="md"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => setOpened(false)}
                            disabled={sendMutation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color={isPeriod ? "indigo" : "black"}
                            leftSection={<IconMailForward size={16} />}
                            onClick={handleSend}
                            loading={sendMutation.isPending}
                            disabled={(selectedEmails.length === 0 && manualEmails.length === 0) || paymentStatuses.length === 0}
                        >
                            {t('send')}
                        </Button>
                    </div>
                }
            >
                <Stack className="space-y-2">
                    {/* Sender Email Select */}
                    {senderEmails.length > 0 && (
                        <Select
                            label={t('select_sender_email')}
                            data={senderEmails}
                            value={selectedFromEmail}
                            onChange={(val) => setSelectedFromEmail(val || '')}
                            allowDeselect={false}
                        />
                    )}

                    {/* Distinct Scope Card */}
                    {isPeriod && (
                        <div className="rounded-xl p-2 border flex items-start gap-3 bg-indigo-50/60 border-indigo-100 text-indigo-950">
                            <div className="p-2 rounded-lg shrink-0 bg-indigo-100 text-indigo-700">
                                <IconCalendarEvent size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                        {t('balance_dates')}
                                    </span>
                                </div>
                                <div className="text-xs font-bold text-slate-800 mt-0.5">
                                    {startDay ? dayjs(startDay).format('DD/MM/YYYY') : '...'}
                                    {' '}{t('to').toLowerCase()}{' '}
                                    {endDay ? dayjs(endDay).format('DD/MM/YYYY') : '...'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payment Status Filter */}
                    {isPeriod && (
                        <div>
                            <Text className="text-xs font-semibold text-gray-700 mb-1.5">{t('payment_status_filter')}</Text>
                            <div className="flex flex-wrap items-center gap-2">
                                {[
                                    { value: 'paid', label: t('paid') },
                                    { value: 'unpaid', label: t('unpaid') },
                                    { value: 'partial', label: t('partially_paid') },
                                ].map(item => {
                                    const isSelected = paymentStatuses.includes(item.value);
                                    return (
                                        <div
                                            key={item.value}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors cursor-pointer select-none ${isSelected ? 'bg-gray-100 border-gray-300 font-semibold' : 'bg-gray-50 border-gray-100 text-gray-400'}`}
                                            onClick={() => togglePaymentStatus(item.value)}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => { }}
                                                size="xs"
                                                color="black"
                                            />
                                            <span className={`text-xs ${isSelected ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}>{item.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div>
                        {hasAnyEmail ? (
                            <div className="space-y-2">
                                {existingEmails.map(email => (
                                    <Checkbox
                                        key={email}
                                        label={email}
                                        checked={selectedEmails.includes(email)}
                                        onChange={(event) => {
                                            if (event.currentTarget.checked) {
                                                setSelectedEmails(prev => [...prev, email]);
                                            } else {
                                                setSelectedEmails(prev => prev.filter(e => e !== email));
                                            }
                                        }}
                                        color="black"
                                    />
                                ))}
                            </div>
                        ) : (
                            <Text className="text-xs text-gray-400 italic">{t('no_emails_available')}</Text>
                        )}
                    </div>

                    <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">
                            {t('enter_additional_emails')}
                        </div>
                        <TagsInput
                            placeholder="example@mail.com"
                            value={manualEmails}
                            onChange={setManualEmails}
                            clearable
                        />
                    </div>

                    <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">
                            {t('custom_message_optional')}
                        </div>
                        <Textarea
                            placeholder={t('custom_message_placeholder')}
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.currentTarget.value)}
                            minRows={3}
                            maxRows={6}
                            autosize
                        />
                    </div>
                </Stack>
            </CardModal>
        </>
    );
}
